/**
 * Dashboard principal para las pruebas de candidatos
 * 
 * Este componente gestiona la navegación entre las diferentes pantallas
 * del proceso de evaluación:
 * - Pantalla de bienvenida
 * - Contenedor de pruebas (bloques A, B, C, D)
 * - Formulario de feedback
 * - Pantalla de finalización
 * 
 * Utiliza el hook useTestState para manejar el estado global
 * de la prueba y la navegación entre pantallas.
 * 
 * @component
 * @author Ana Lizcano
 */

// src/layouts/candidate-test/index.js
import React, { useMemo } from 'react';
import useTestState from './hooks/useTestState';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import PageLayout from "examples/LayoutContainers/PageLayout";
import Footer from "examples/Footer";

// Componentes del sistema de pruebas
import WelcomeScreen from './components/WelcomeScreen';
import TestContainer from './components/TestContainer';
import FeedbackForm from './components/FeedbackForm';
import CompletionScreen from './components/CompletionScreen';

/**
 * Componente principal del dashboard de candidatos
 * Maneja la navegación entre las diferentes etapas de la prueba
 */
const CandidateDashboard = () => {
  const testStateManager = useTestState();

  /**
   * Renderiza la pantalla actual basada en el estado de navegación
   * @returns {JSX.Element} Componente de la pantalla actual
   */
  const renderCurrentScreen = () => {
    switch (testStateManager.currentScreen) {
      case 'welcome':
        // Pantalla inicial con instrucciones y botón de inicio
        return <WelcomeScreen onStart={() => testStateManager.navigateToScreen('test')} />;
      
      case 'test':
        // Contenedor principal de las pruebas (bloques A, B, C, D)
        return <TestContainer testStateManager={testStateManager} />;
      
      case 'feedback':
        // Formulario para recopilar feedback del candidato
        return (
          <FeedbackForm 
            feedbackState={testStateManager.feedbackState}
            onUpdateFeedback={testStateManager.updateFeedback}
            onSubmit={() => testStateManager.navigateToScreen('completion')}
          />
        );
      
      case 'completion':
        // Pantalla final de agradecimiento y confirmación
        return <CompletionScreen />;
      
      default:
        // Fallback a la pantalla de bienvenida
        return <WelcomeScreen onStart={() => testStateManager.navigateToScreen('test')} />;
    }
  };

  return (
    <PageLayout>
      <MDBox py={3}>
        <MDBox 
          sx={{ 
            minHeight: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {renderCurrentScreen()}
        </MDBox>
      </MDBox>
      <Footer />
    </PageLayout>
  );
};

export default CandidateDashboard;